import { z } from 'zod';
import { isoDateSchema } from './common.js';
import { signedUrlSchema, uploadUrlSchema } from './files.js';

/**
 * The world manager (docs/DESIGN.md §7): worlds are folders in the server's root, found
 * through the profile's `files.worldFolders`, and every action is built on the file API.
 */

/** An existing world's folder name: one path segment, never hidden or a dot path. */
export const worldNameSchema = z
  .string()
  .min(1)
  .max(100)
  .refine(
    (n) => !/[/\\\0]/.test(n) && !n.startsWith('.') && n.trim() === n,
    'Not a world folder name.',
  );

/** A name for a new (uploaded) world, kept to characters every game and OS accepts. */
export const newWorldNameSchema = z
  .string()
  .trim()
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/,
    'Use up to 64 letters, digits, dots, dashes or underscores, starting with a letter or digit.',
  );

export const worldParamsSchema = z.object({ server: z.string(), world: worldNameSchema });

export const worldDimensionSchema = z.object({
  /** The folder in the server's root, e.g. "world_nether". */
  folder: z.string(),
  size: z.number().int(),
  files: z.number().int(),
  modifiedAt: isoDateSchema,
});

export const worldSchema = z.object({
  name: z.string(),
  /** The world level-name points at: the one the game loads. */
  active: z.boolean(),
  size: z.number().int(),
  modifiedAt: isoDateSchema,
  /** Only the dimension folders that exist. */
  dimensions: z.array(worldDimensionSchema),
});
export type World = z.infer<typeof worldSchema>;

export const SERVER_POWER_STATES = ['offline', 'starting', 'running', 'stopping'] as const;

export const worldListSchema = z.object({
  /** What the game will load: server.properties' level-name, or the game's default. */
  levelName: z.string(),
  /** False when server.properties doesn't set level-name (or doesn't exist yet). */
  levelNameSet: z.boolean(),
  propertiesExists: z.boolean(),
  /** The game creates the active world on start when its folder doesn't exist. */
  activeExists: z.boolean(),
  /** The profile's dimension patterns, e.g. ["{world}", "{world}_nether"]. */
  dimensions: z.array(z.string()),
  state: z.enum(SERVER_POWER_STATES),
  worlds: z.array(worldSchema),
});
export type WorldList = z.infer<typeof worldListSchema>;

/** Destructive actions repeat the world's name, checked on the server. */
export const worldConfirmSchema = z.object({ confirm: z.string().max(200) });

export const worldDeleteResultSchema = z.object({
  /** The folders that were removed. */
  deleted: z.array(z.string()),
});

export const worldActivateResultSchema = z.object({
  levelName: z.string(),
  changed: z.boolean(),
});

export const worldDownloadResultSchema = signedUrlSchema.extend({
  /** The archive made in the server's root; it stays there until someone deletes it. */
  archive: z.string(),
  size: z.number().int(),
});

export const worldUploadSchema = z.object({ name: newWorldNameSchema });
export const worldUploadResultSchema = uploadUrlSchema.extend({
  /** The new folder the archive goes into, e.g. "/survival". */
  folder: z.string(),
});

export const worldExtractSchema = z.object({
  /** The uploaded archive's file name inside the new folder. */
  archive: z
    .string()
    .min(1)
    .max(255)
    .refine((n) => !/[/\\\0]/.test(n) && n !== '.' && n !== '..', 'Not a file name.'),
});
export const worldExtractResultSchema = z.object({
  world: worldSchema,
  /** Top-level entries of the archive that weren't part of the world and were removed. */
  discarded: z.array(z.string()),
});
